import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ReferenceButton from './ReferenceButton';

export interface ReferenceOptions {
  onReferenceClick?: (referenceId: string) => void;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    reference: {
      setReference: (attributes: { referenceId: string; text: string }) => ReturnType;
    };
  }
}

export const Reference = Node.create<ReferenceOptions>({
  name: 'reference',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      onReferenceClick: undefined,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      referenceId: {
        default: null,
        parseHTML: element => element.getAttribute('data-reference-id'),
        renderHTML: attributes => {
          if (!attributes.referenceId) {
            return {};
          }
          return {
            'data-reference-id': attributes.referenceId,
          };
        },
      },
      text: {
        default: '',
        parseHTML: element => element.textContent,
        renderHTML: attributes => {
          return {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[href^="#reference-"]',
        getAttrs: (dom) => {
          const href = (dom as HTMLElement).getAttribute('href');
          const referenceId = href?.replace('#reference-', '') || null;
          const text = (dom as HTMLElement).textContent || '';
          return { referenceId, text };
        },
      },
      {
        tag: 'span[data-reference-id]',
        getAttrs: (dom) => {
          const referenceId = (dom as HTMLElement).getAttribute('data-reference-id');
          const text = (dom as HTMLElement).textContent || '';
          return { referenceId, text };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-reference-id': node.attrs.referenceId,
        'data-reference-link': 'true',
      }),
      node.attrs.text,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ReferenceButton);
  },

  addCommands() {
    return {
      setReference:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
    };
  },
});